import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import {
	Splitter,
	Button,
	Space,
	message,
	Select,
	notification,
	Tooltip,
	Typography,
} from 'antd';
import {
	BarChartOutlined,
	CheckCircleOutlined,
	SaveOutlined,
	SyncOutlined,
	CopyOutlined,
	DownloadOutlined,
	PlusOutlined,
	ReloadOutlined,
	OpenAIOutlined,
	TableOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import { PiLineSegment, PiPolygon } from 'react-icons/pi';
import Header from '../../components/Header';
import BalloonTable from '../../components/BalloonTable';
import BallooningViewer, { ImageLoadState } from '../../components/BallooningViewer';
import { ArcSagittaWidget } from '../../components/ArcSagittaWidget';
import {
	useUpdateBallooningProjectMutation,
	useGetBallooningProjectQuery,
} from '../../redux/api/ballooning';
import {
	Balloon,
	BalloonGenerationOptions,
	BallooningPage,
	ToleranceTable,
	UnverifiedReuseSource,
} from '../../interfaces/Ballooning';
import { useSelector } from 'react-redux';
import { Modal } from 'antd';
import BallooningReusabilityChecker from '../../components/BallooningReusabilityChecker';
import {
	useCreateBallooningProjectMutation,
	useSetupInspectionPlanMutation,
	useResetInspectionPlanMutation,
	useValidateBallooningChangesMutation,
	useRefreshPreviousOrdersMutation,
	useGenerateBalloonsMutation,
	useCancelGenerationMutation,
	useGetGenerationJobQuery,
	useApproveBalloonsMutation,
	useDevUploadDrawingMutation,
} from '../../redux/api/ballooning.ts';
import { Alert, Badge, Switch, Tag } from 'antd';
import {
	approvalEdits,
	countUnreviewed,
	markIndividualEdit,
	markMeasurementsSaved,
	measurementPayload,
	mergeReviewState,
} from '../../utils/balloonReview';
import BalloonQuickEdit, {
	BalloonMeasurementUpdate,
} from '../../components/BalloonQuickEdit';
import ToleranceTableModal from '../../components/ToleranceTableModal';
import GenerationOptionsModal from '../../components/GenerationOptionsModal';
import GenerationStatsModal from '../../components/GenerationStatsModal';
import GenerationProgressModal from '../../components/GenerationProgressModal';
import BalloonReviewPanel from '../../components/BalloonReviewPanel';

const REVIEW_PANEL_STORAGE_KEY = 'ballooning.reviewPanel';
import { applyNominalEdit, applyTableToBalloons, hasRows } from '../../utils/toleranceTable';
import { viewportCappedModalStyles } from '../../utils/modalStyles';
import { missingFields } from '../../components/BalloonTable/measurementFields';
import { useGetGageTypesQuery } from '../../redux/api/quality';

import { useUpdateOrderLineItemMutation } from '../../redux/api/order.ts';
import { useEnsureProgramMutation } from '../../redux/api/manufacturing';
import IssueFormModal from '../CamProgramming/IssueFormModal';
import '../CamProgramming/styles.css';

const { Option } = Select;

type RootState = {
	user: {
		authToken: string;
	};
};

const unitOptions = [
	{ value: '', label: 'Select a unit', disabled: true },
	{ value: 'INCH', label: 'Inch' },
	{ value: 'MM', label: 'Millimeter' },
];
const balloonSizeOptions = [
	{ value: 30, label: 'Extra Small' },
	{ value: 40, label: 'Small' },
	{ value: 50, label: 'Medium' },
	{ value: 60, label: 'Large' },
	{ value: 70, label: 'Extra Large' },
];

const summarizeNumbers = (numbers: number[], shown = 8) =>
	numbers.length > shown
		? `${numbers.slice(0, shown).join(', ')} and ${numbers.length - shown} more`
		: numbers.join(', ');

const BallooningPage: React.FC = () => {
	const navigate = useNavigate();
	const { id: projectId } = useParams();
	const { data: projectData, refetch } = useGetBallooningProjectQuery(
		{
			id: projectId!,
		},
		{
			skip: !projectId,
			refetchOnMountOrArgChange: true,
		}
	);
	const headerRef = useRef<HTMLDivElement>(null);
	const tablePanelRef = useRef<HTMLDivElement>(null);

	const [createBallooningProject] = useCreateBallooningProjectMutation();

	const userLayoutPreference = localStorage.getItem('ballooningLayout');

	const [layout, setLayout] = useState<'vertical' | 'horizontal'>(
		userLayoutPreference &&
			(userLayoutPreference === 'vertical' ||
				userLayoutPreference === 'horizontal')
			? userLayoutPreference
			: 'vertical'
	);

	// State for project settings. The unit starts empty on purpose: pre-selecting
	// INCH means metric parts get ballooned in millimetres under an inch label,
	// which the Keyence check then reports as a failure on every dimension.
	const [unit, setUnit] = useState('');
	const [balloonSize, setBalloonSize] = useState(50);
	const [toleranceTable, setToleranceTable] = useState<ToleranceTable | null>(null);
	const [toleranceSuggestion, setToleranceSuggestion] =
		useState<ToleranceTable | null>(null);
	const [toleranceModalOpen, setToleranceModalOpen] = useState(false);
	const [statsModalOpen, setStatsModalOpen] = useState(false);
	const [autoSkipDismissed, setAutoSkipDismissed] = useState(false);

	// Local state for balloons
	const [localBalloons, setLocalBalloons] = useState<Balloon[]>([]);
	const [originalBalloons, setOriginalBalloons] = useState<Balloon[]>([]);
	const [deletedBalloonIds, setDeletedBalloonIds] = useState<any[]>([]);
	const [activeBalloonId, setActiveBalloonId] = useState<string | null>(null);
	const [centerOnActive, setCenterOnActive] = useState(false);
	const unreviewedCount = countUnreviewed(localBalloons);

	const { data: gageTypes } = useGetGageTypesQuery({});
	const incompleteBalloons = useMemo(() => {
		// Without the gages, thread gages can't be told apart and would all look incomplete.
		if (!gageTypes) return [];
		const gages = Object.fromEntries(gageTypes.map((gage) => [gage.id, gage]));
		return localBalloons
			.map((balloon) => ({ balloon, missing: missingFields(balloon, gages) }))
			.filter(({ missing }) => missing.length > 0)
			.sort((a, b) => a.balloon.balloon_number - b.balloon.balloon_number);
	}, [localBalloons, gageTypes]);
	const showMissingFields = () =>
		balloonTableRef.current?.showMissingFields(
			incompleteBalloons.flatMap(({ balloon, missing }) =>
				missing.map((m) => ({ ...m, balloonId: balloon.id }))
			)
		);
	const completionBlocker =
		unreviewedCount > 0
			? `${unreviewedCount} AI balloon${unreviewedCount === 1 ? '' : 's'} still need${unreviewedCount === 1 ? 's' : ''} review.`
			: incompleteBalloons.length > 0
				? `Balloon${incompleteBalloons.length === 1 ? '' : 's'} ${summarizeNumbers(
					incompleteBalloons.map(({ balloon }) => balloon.balloon_number)
				)} ${incompleteBalloons.length === 1 ? 'is' : 'are'} missing required fields (marked in red).`
				: null;

	const [updateBallooningProject, { isLoading, error }] =
		useUpdateBallooningProjectMutation();
	const [validateChanges] = useValidateBallooningChangesMutation();
	const [updateOrderLineItem] = useUpdateOrderLineItemMutation();
	const [setupInspectionPlan] = useSetupInspectionPlanMutation();
	const [resetInspectionPlan] = useResetInspectionPlanMutation();
	const [refreshPreviousOrders, { isLoading: isRefreshingPreviousOrders }] =
		useRefreshPreviousOrdersMutation();
	const [ensureProgram, { isLoading: isEnsuringProgram }] =
		useEnsureProgramMutation();
	const [issueModalProgramId, setIssueModalProgramId] = useState<
		string | null
	>(null);

	const [generateBalloons, { isLoading: isStartingGeneration }] =
		useGenerateBalloonsMutation();
	const [approveBalloons] = useApproveBalloonsMutation();
	const [jobPollingInterval, setJobPollingInterval] = useState(0);
	const { data: generationJob, refetch: refetchGenerationJob } =
		useGetGenerationJobQuery(
			{ id: projectId ?? '' },
			{
				skip: !projectId,
				pollingInterval: jobPollingInterval,
				refetchOnMountOrArgChange: true,
			}
		);
	const generationActive =
		generationJob?.status === 'pending' || generationJob?.status === 'running';
	const [dismissedJobErrorId, setDismissedJobErrorId] = useState<string | null>(
		null
	);
	const previousJobStatus = useRef<string | undefined>(undefined);

	useEffect(() => {
		setJobPollingInterval(generationActive ? 2000 : 0);
	}, [generationActive]);

	useEffect(() => {
		const status = generationJob?.status;
		const wasActive =
			previousJobStatus.current === 'pending' ||
			previousJobStatus.current === 'running';
		previousJobStatus.current = status;
		if (!wasActive) return;
		if (status === 'cancelled') {
			// Balloons may already be saved when the cancel came during view tracing.
			refetch();
			notification.info({
				message: 'Analysis cancelled',
				description: 'Generate the inspection plan again whenever you are ready.',
			});
		}
		if (status === 'succeeded') {
			refetch();
			const count = generationJob?.balloons_created ?? 0;
			notification.success({
				message: 'Inspection plan draft generated',
				description:
					count > 0
						? `${count} balloon${count === 1 ? '' : 's'} created. Each one is flagged "Needs review" until you approve or edit it.`
						: 'No dimensions were found on the drawing. Add balloons manually.',
			});
		}
	}, [generationJob?.status]);

	const [cancelGeneration, { isLoading: isCancellingGeneration }] =
		useCancelGenerationMutation();

	const handleCancelGeneration = async () => {
		if (!projectId) return;
		try {
			await cancelGeneration({ id: projectId }).unwrap();
		} catch (err: any) {
			// 409: it finished or was cancelled meanwhile; the refetch shows which.
			if (err?.status !== 409) {
				notification.error({
					message: 'Could not cancel the analysis',
					description: err?.data?.detail || 'Unexpected error. Try again.',
				});
			}
		}
		refetchGenerationJob();
	};

	const [generationOptionsOpen, setGenerationOptionsOpen] = useState(false);

	const handleGenerateBalloons = async (options: BalloonGenerationOptions) => {
		if (!projectId) return;
		try {
			await generateBalloons({ id: projectId, options }).unwrap();
			setGenerationOptionsOpen(false);
			setJobPollingInterval(2000);
			refetchGenerationJob();
		} catch (err: any) {
			notification.error({
				message: 'Could not start generation',
				description:
					err?.data?.detail || 'Unexpected error while queueing the analysis.',
			});
			refetchGenerationJob();
		}
	};

	const [showViewOutlines, setShowViewOutlines] = useState(false);
	const [showLeaderLines, setShowLeaderLines] = useState(true);
	const [drawingLoadState, setDrawingLoadState] = useState<ImageLoadState>('idle');

	const [devUploadDrawing, { isLoading: isUploadingDrawing }] =
		useDevUploadDrawingMutation();
	const devDrawingInputRef = useRef<HTMLInputElement>(null);

	const uploadDevDrawing = async (file: File, replaceBalloons: boolean) => {
		if (!projectId) return;
		try {
			const { page_count } = await devUploadDrawing({
				id: projectId,
				file,
				replaceBalloons,
			}).unwrap();
			setPageNumber(1);
			setDeletedBalloonIds([]);
			await refetch();
			message.success(
				`Drawing uploaded: ${page_count} page${page_count === 1 ? '' : 's'}.`
			);
		} catch (err: any) {
			if (err?.status === 404) {
				message.error(
					'Dev drawing upload is disabled on this backend (needs DEBUG=True and ENVIRONMENT=development).'
				);
				return;
			}
			message.error(err?.data?.detail || 'Failed to upload the drawing.');
		}
	};

	const handleDevDrawingSelected = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0];
		event.target.value = '';
		if (!file) return;
		const savedBalloonCount = originalBalloons.length;
		if (savedBalloonCount === 0 && !hasUnsavedChanges()) {
			uploadDevDrawing(file, false);
			return;
		}
		Modal.confirm({
			title: 'Replace the drawing?',
			content:
				savedBalloonCount > 0
					? `This deletes the project's ${savedBalloonCount} balloon(s) and their inspection results, then replaces the pages. Unsaved changes are discarded.`
					: 'Unsaved changes are discarded.',
			okText: 'Replace',
			okButtonProps: { danger: true },
			onOk: () => uploadDevDrawing(file, savedBalloonCount > 0),
		});
	};

	const confirmBreakingChanges = (warnings: string[]) =>
		new Promise<boolean>((resolve) => {
			Modal.confirm({
				title: 'Warning: Breaking Changes',
				content: (
					<div>
						<p>Your changes will affect existing inspection data:</p>
						<ul>
							{warnings.map((w, i) => (
								<li key={i}>{w}</li>
							))}
						</ul>
						<p>Do you want to proceed?</p>
					</div>
				),
				okText: 'Yes, Apply Changes',
				okType: 'danger',
				cancelText: 'Cancel',
				onOk: () => resolve(true),
				onCancel: () => resolve(false),
			});
		});

	// Approving also saves the measurement edits made to those balloons; anything
	// else unsaved (other rows, moves, renumbers) still waits for Save.
	const handleApproveBalloons = async (ids: string[]): Promise<boolean> => {
		const edits = approvalEdits(ids, localBalloons, originalBalloons);
		if (edits.length && !balloonTableRef.current?.validate(edits.map((e) => e.id))) {
			message.error('Fix the fields marked in red before approving.');
			return false;
		}
		const sent = localBalloons.filter((b) => edits.some((e) => e.id === b.id));
		try {
			const approved = await approveBalloons({ projectId: projectId!, ids, edits }).unwrap();
			setLocalBalloons((prev) => mergeReviewState(prev, approved));
			setOriginalBalloons((prev) =>
				mergeReviewState(markMeasurementsSaved(prev, sent), approved)
			);
			message.success(
				`${ids.length} balloon${ids.length === 1 ? '' : 's'} marked as reviewed.`
			);
			return true;
		} catch (err: any) {
			const data = err?.data;
			message.error(
				data?.detail || data?.non_field_errors?.[0] || data?.edits?.[0] || data?.ids?.[0] ||
					'Failed to approve balloons.'
			);
			return false;
		}
	};

	const isBalloonEditable = (balloon: Balloon) =>
		ballooningStatus !== 'Completed' &&
		allowedEditableBalloonId !== 'NONE' &&
		(!allowedEditableBalloonId || allowedEditableBalloonId === balloon.id);

	const [reviewPanelOpen, setReviewPanelOpen] = useState(
		() => localStorage.getItem(REVIEW_PANEL_STORAGE_KEY) !== 'closed'
	);
	const handleReviewPanelOpen = (open: boolean) => {
		setReviewPanelOpen(open);
		localStorage.setItem(REVIEW_PANEL_STORAGE_KEY, open ? 'open' : 'closed');
	};

	const handleQuickEdit = (id: string, updates: BalloonMeasurementUpdate) => {
		setLocalBalloons((prev) =>
			prev.map((b) => {
				if (b.id !== id) return b;
				if ('nominal' in updates) {
					return applyNominalEdit(b, updates.nominal ?? null, toleranceTable);
				}
				const touchesTolerance =
					'tolerance_plus' in updates || 'tolerance_minus' in updates;
				return markIndividualEdit({
					...b,
					...(updates as unknown as Partial<Balloon>),
					...(touchesTolerance ? { tolerance_source: 'manual' as const } : {}),
				});
			})
		);
	};

	const handleLogIssue = async () => {
		const orderLineItemId = projectData?.order_line_item?.id;
		if (!orderLineItemId) {
			message.error('Order line item is missing');
			return;
		}
		try {
			const program = await ensureProgram({
				order_line_item: orderLineItemId,
				program_type: 'inspection_plan',
			}).unwrap();
			setIssueModalProgramId(program.id);
		} catch {
			message.error('Failed to prepare inspection plan program');
		}
	};

	const handleSetupPlan = async (projectId: string, action: 'setup' | 'reset') => {
		try {
			if (action === 'setup') {
				await setupInspectionPlan({ id: projectId }).unwrap();
			} else {
				await resetInspectionPlan({ id: projectId }).unwrap();
			}
		} catch (error) {
			console.error(`Failed to ${action} inspection plan:`, error);
			message.error(`Failed to ${action} inspection plan`);
		}
	};

	const token = useSelector((state: RootState) => state.user.authToken);

	const [unitError, setUnitError] = useState(false);

	const [pageNumber, setPageNumber] = useState<number>(1);
	const [pageID, setPageID] = useState<string | null>(null);

	const [ballooningStatus, setBallooningStatus] =
		useState<string>('In Progress');
	const [completionModalOpen, setCompletionModalOpen] = useState(false);

	const [isModalOpen, setIsModalOpen] = useState(false);
	const [isModalAlreadyOpened, setIsModalAlreadyOpened] = useState(false);
	const [isUserRequest, setIsUserRequest] = useState(false);
	const [shouldSave, setShouldSave] = useState(false);
	const [isDataReady, setIsDataReady] = useState(false);
	const [reusedFrom, setReusedFrom] = useState<string | null>(null);
	const balloonTableRef = useRef<any>();



	useEffect(() => {
		if (projectData?.order_line_item?.ballooning_status) {
			setBallooningStatus(projectData.order_line_item.ballooning_status);
		}
	}, [projectData]);



	const handleChangeUnit = (value: string) => {
		setUnit(value);
		setUnitError(false);
	};

	const handleChangeBalloonSize = (value: number) => {
		setBalloonSize(value);
	};

	const hasTemporaryBalloons = () => {
		return localBalloons.some(
			(balloon) => balloon.id?.toString().startsWith('temp-')
		);
	};

	const hasSavedBalloons = () => {
		return localBalloons.some(
			(balloon) => !balloon.id?.toString().startsWith('temp-')
		);
	};

	const handleApproval = (
		balloons: React.SetStateAction<Balloon[]>,
		reusedFromId: string | null
	) => {
		setLocalBalloons(balloons);
		setReusedFrom(reusedFromId);
		setShouldSave(true);
	};

	const uniqueViews = useMemo(() => {
		const allViews = localBalloons
			.map((balloon) => balloon.view)
			.filter(Boolean);
		return Array.from(new Set(allViews));
	}, [localBalloons]);

	const handleCopy = (view: string) => {
		const derivedString = `${projectData?.order_line_item.order
			.name}_${projectData?.order_line_item.name}_${view.toUpperCase()}`;

		navigator.clipboard
			.writeText(derivedString)
			.then(() => {
				message.success(`Copied to clipboard: ${derivedString}`);
			})
			.catch(() => {
				message.error('Failed to copy');
			});
	};

	const rebuildProject = async () => {
		if (!projectData?.order_line_item?.id) {
			notification.error({
				message: 'Rebuild Failed',
				description: 'Order line item ID is missing.',
			});
			return;
		}

		const lineItemId = projectData.order_line_item.id;
		const lineItemName = projectData.order_line_item.name;

		const key = 'rebuilding-project';
		message.loading({
			content: `Rebuilding project for ${lineItemName}...`,
			key,
			duration: 0,
		});

		try {
			await createBallooningProject({
				body: { order_line_item: lineItemId },
			}).unwrap();

			await refetch();

			message.success({
				content: `Project for ${lineItemName} rebuilt successfully!`,
				key,
				duration: 3,
			});
		} catch (error) {
			const errorMessage =
				error?.data?.detail || 'An unexpected error occurred.';
			message.error({
				content: `Failed to rebuild project: ${errorMessage}`,
				key,
				duration: 5,
			});
			console.error('Failed to rebuild project:', error);
		}
	};

	useEffect(() => {
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.altKey && event.code === 'KeyR') {
				event.preventDefault();
				rebuildProject();
			}
		};

		window.addEventListener('keydown', handleKeyDown);

		return () => {
			window.removeEventListener('keydown', handleKeyDown);
		};
	}, [rebuildProject]);

	useEffect(() => {
		if (shouldSave) {
			handleSave().finally(() => {
				setShouldSave(false);
			});
		}
	}, [localBalloons, shouldSave]);

	useEffect(() => {
		if (projectData?.order_line_item?.ballooning_status) {
			setBallooningStatus(projectData.order_line_item.ballooning_status);
		}
	}, [projectData?.order_line_item?.ballooning_status]);

	useEffect(() => {
		if (projectData) {
			setUnit(projectData.unit ?? '');
			setBalloonSize(projectData.balloon_size);
			setToleranceTable(projectData.tolerance_table ?? null);
			setToleranceSuggestion(projectData.tolerance_table_suggestion ?? null);
		}

		if (projectData && projectData.ballooning_pages) {
			const allBalloons = projectData.ballooning_pages.flatMap(
				(page) => page.balloons || []
			);
			setLocalBalloons(allBalloons);
			setOriginalBalloons(JSON.parse(JSON.stringify(allBalloons)));
			setIsDataReady(true);

			const selectedPage = projectData.ballooning_pages.find(
				(page) => page.page_number === pageNumber
			);

			if (selectedPage) {
				setPageID(selectedPage.id);
			}
		}
	}, [projectData]);

	useEffect(() => {
		if (projectData && projectData.ballooning_pages) {
			const selectedPage = projectData.ballooning_pages.find(
				(page) => page.page_number === pageNumber
			);

			if (selectedPage) {
				setPageID(selectedPage.id);
			}
		}
	}, [pageNumber]);

	useEffect(() => {
		if (isDataReady) {
			if (
				projectData?.previous_ballooning_projects?.length > 0 &&
				!isModalAlreadyOpened &&
				!hasSavedBalloons()
			) {
				setIsModalOpen(true);
				setIsModalAlreadyOpened(true);
			}
		}
	}, [projectData, isModalAlreadyOpened, isDataReady]);

	// Helper for robust value comparison
	const areValuesEqual = (v1: any, v2: any) => {
		const normalize = (v: any) => (v === '' || v === undefined ? null : v);
		const n1 = normalize(v1);
		const n2 = normalize(v2);

		if (n1 === n2) return true;
		// eslint-disable-next-line eqeqeq
		if (n1 != null && n2 != null && n1 == n2) return true;
		return false;
	};

	const allowedEditableBalloonId = useMemo(() => {
		if (!projectData?.is_locked) return null;

		if (deletedBalloonIds.length > 0) return 'NONE';

		const addedBalloon = localBalloons.find((b) =>
			b.id?.toString().startsWith('temp-')
		);
		if (addedBalloon) return addedBalloon.id;

		for (const local of localBalloons) {
			const original = originalBalloons.find((b) => b.id === local.id);
			if (!original) continue;

			const fields: (keyof Balloon)[] = [
				'nominal',
				'nominal_decimals',
				'tolerance_plus',
				'tolerance_minus',
				'measurement_strategy',
				'measurement_type',
				'gage_type',
				'text_value',
				'view',
				'keyence_device',
				'coordinate_x',
				'coordinate_y',
				'gdt_type',
			];

			const isModified = fields.some((field) => {
				const v1 = local[field];
				const v2 = original[field];
				return !areValuesEqual(v1, v2);
			});

			if (isModified) return local.id;
		}

		return null;
	}, [localBalloons, originalBalloons, deletedBalloonIds, projectData?.is_locked]);

	/**
	 * Build the inspection plan, stopping for a confirmation when the balloons were
	 * imported from a part that is not matched to this line item. That is the step
	 * where their nominals and tolerances become inspection criteria, so it is the
	 * last chance to catch values that belong to the other print. Returns false when
	 * the operator backs out; throws on genuine failures.
	 */
	const setupPlanConfirmingUnverifiedReuse = async (
		id: string
	): Promise<boolean> => {
		try {
			await setupInspectionPlan({ id }).unwrap();
			return true;
		} catch (err: any) {
			if (err?.status !== 409 || !err?.data?.confirmation_required) {
				throw err;
			}

			const source: UnverifiedReuseSource | undefined =
				err.data.unverified_reuse_source;
			const sourceLabel = [source?.order_name, source?.order_line_item_name]
				.filter(Boolean)
				.join(' — ');

			const confirmed = await new Promise<boolean>((resolve) => {
				Modal.confirm({
					title: 'These balloons came from a different part',
					width: 560,
					content: (
						<div>
							<p>
								They were imported from{' '}
								<strong>{sourceLabel || 'another ballooning project'}</strong>,
								which is not matched to this line item, so the nominals and
								tolerances are that part's.
							</p>
							<p>
								Completing now turns them into this part's inspection plan, its
								AQL samples and its FAI. Confirm only if you checked every
								balloon against this part's drawing.
							</p>
						</div>
					),
					okText: 'I checked them — complete it',
					okType: 'danger',
					cancelText: 'Go back and review',
					onOk: () => resolve(true),
					onCancel: () => resolve(false),
				});
			});

			if (!confirmed) {
				message.info(
					'Left in progress. Review the imported balloons, then set the status to Completed again.'
				);
				return false;
			}

			await setupInspectionPlan({
				id,
				acknowledgeUnverifiedReuse: true,
			}).unwrap();
			return true;
		}
	};

	const updateBallooningStatus = async (newStatus: string) => {
		try {
			/* if (projectData.is_locked) {
				message.error({
					content:
						'Project is locked because some samples for this product have been measured already.',
					duration: 5,
				});
				return;
			} */

			if (!projectData?.order_line_item?.id) {
				message.error('Project data or order ID is missing');
				return;
			}

			const orderId = projectData.order_line_item.id;
			const prevStatus = ballooningStatus || '';

			const unreviewed = countUnreviewed(localBalloons);
			if (newStatus === 'Completed' && unreviewed > 0) {
				Modal.warning({
					title: `${unreviewed} auto-generated balloon${unreviewed === 1 ? ' needs' : 's need'} review`,
					width: 520,
					content: (
						<p>
							Their nominals and tolerances were read from the drawing by AI and
							become this part&apos;s inspection criteria on completion. Approve or
							correct every one before completing ballooning.
						</p>
					),
					okText: 'Go back and review',
				});
				return;
			}

			if (newStatus === 'Completed' && incompleteBalloons.length > 0) {
				showMissingFields();
				Modal.warning({
					title: 'Some balloons are missing required fields',
					width: 520,
					content: (
						<>
							<p>Fill in the fields marked in red before completing ballooning:</p>
							<ul style={{ paddingLeft: 18, marginBottom: 0 }}>
								{incompleteBalloons.slice(0, 10).map(({ balloon, missing }) => (
									<li key={balloon.id}>
										Balloon {balloon.balloon_number}:{' '}
										{missing.map((m) => m.label).join(', ')}
									</li>
								))}
								{incompleteBalloons.length > 10 && (
									<li>and {incompleteBalloons.length - 10} more</li>
								)}
							</ul>
						</>
					),
					okText: 'Go back and fix',
				});
				return;
			}

			// Save first so the server's completion check sees the balloons as shown here.
			// Refetching after this save is safe: the status has not changed yet.
			if (newStatus === 'Completed' && hasUnsavedChanges() && !(await handleSave())) {
				return;
			}

			// If we are moving away from Completed, reset the inspection plan
			// but ONLY if the project is NOT locked. If it is locked, we want to keep existing measurements.
			if (prevStatus === 'Completed' && newStatus !== 'Completed') {
				if (!projectData.is_locked) {
					await handleSetupPlan(projectData.id, 'reset');
				}
			}

			// Update the status on the server BEFORE handleSave, because handleSave
			// triggers a refetch that would overwrite local state with the old status.
			const payload = {
				id: orderId,
				values: { ballooning_status: newStatus },
			};

			await updateOrderLineItem(payload).unwrap();
			setBallooningStatus(newStatus);

			const saveSucceeded = await handleSave();
			if (!saveSucceeded) {
				// Save failed; revert the status change.
				await updateOrderLineItem({
					id: orderId,
					values: { ballooning_status: prevStatus },
				}).unwrap();
				setBallooningStatus(prevStatus);
				return;
			}

			message.success(
				`Ballooning status updated to ${newStatus} successfully!`
			);

			if (newStatus === 'Completed') {
				let planReady = false;
				try {
					planReady = await setupPlanConfirmingUnverifiedReuse(projectData.id);
				} catch (e) {
					// Setup failed — revert ballooning status so the user can retry
					message.error('Failed to setup inspection plan. Reverting status.');
				}
				if (!planReady) {
					await updateOrderLineItem({
						id: orderId,
						values: { ballooning_status: 'In Progress' },
					}).unwrap();
					setBallooningStatus('In Progress');
					return;
				}
				setCompletionModalOpen(true);
			}
		} catch (error) {
			console.error('Error updating ballooning status:', error);

			const serverMessage = (error as { data?: { message?: unknown } })?.data?.message;
			if (typeof serverMessage === 'string') {
				message.error(serverMessage);
			} else if (error instanceof Error) {
				message.error(`Failed to update ballooning status: ${error.message}`);
			} else {
				message.error(
					'Failed to update ballooning status due to an unknown error'
				);
			}
		}
	};



	const toleranceTableChanged = () =>
		JSON.stringify(toleranceTable ?? null) !==
		JSON.stringify(projectData?.tolerance_table ?? null);
	const toleranceSuggestionChanged = () =>
		JSON.stringify(toleranceSuggestion ?? null) !==
		JSON.stringify(projectData?.tolerance_table_suggestion ?? null);

	const handleApplyToleranceTable = (
		table: ToleranceTable | null,
		usedSuggestion: boolean
	) => {
		setToleranceTable(table);
		if (usedSuggestion) setToleranceSuggestion(null);
		const { balloons, changed } = applyTableToBalloons(localBalloons, table);
		if (changed) setLocalBalloons(balloons);
		setToleranceModalOpen(false);
		message.info(
			changed
				? `Tolerance table updated; ${changed} balloon${changed === 1 ? '' : 's'} re-toleranced. Save to keep the changes.`
				: 'Tolerance table updated. Save to keep the changes.'
		);
	};

	const hasUnsavedChanges = useCallback(() => {
		// A project with no unit yet reads as '' here, so normalize before
		// comparing or an untouched project looks permanently dirty.
		if (unit !== (projectData?.unit ?? '')) return true;
		if (balloonSize !== projectData?.balloon_size) return true;
		if (toleranceTableChanged() || toleranceSuggestionChanged()) return true;
		if (deletedBalloonIds.length > 0) return true;

		// Check for added balloons (temp ids)
		const addedBalloon = localBalloons.find((b) =>
			b.id?.toString().startsWith('temp-')
		);
		if (addedBalloon) return true;

		if (localBalloons.length !== originalBalloons.length) return true;

		// Check for modifications
		for (const local of localBalloons) {
			const original = originalBalloons.find((b) => b.id === local.id);
			if (!original) return true;

			const fields: (keyof Balloon)[] = [
				'nominal',
				'nominal_decimals',
				'tolerance_plus',
				'tolerance_minus',
				'tolerance_source',
				'measurement_strategy',
				'measurement_type',
				'gage_type',
				'text_value',
				'view',
				'keyence_device',
				'coordinate_x',
				'coordinate_y',
				'balloon_number'
			];

			const isModified = fields.some((field) => {
				const v1 = local[field] ?? null;
				const v2 = original[field] ?? null;
				// Use loose equality to handle null/undefined differences and string/number differences
				return v1 != v2;
			});

			if (isModified) return true;
		}

		return false;
	}, [
		unit,
		balloonSize,
		localBalloons,
		originalBalloons,
		deletedBalloonIds,
		projectData,
		toleranceTable,
		toleranceSuggestion,
	]);

	const handleSave = async (): Promise<boolean> => {
		if (!hasUnsavedChanges()) {
			return true;
		}

		if (!unit) {
			setUnitError(true);
			message.error('Please select a Unit before saving.');
			return false;
		}
		setUnitError(false);

		if (!balloonTableRef.current?.validate()) {
			notification.error({
				message: 'Validation Error',
				description:
					'Please fix the fields marked in red. Your changes were not saved.',
			});
			return false;
		}

		const balloonsPayload = localBalloons.map((b) => {
			const balloon: any = {
				...measurementPayload(b),
				coordinate_x: b.coordinate_x,
				coordinate_y: b.coordinate_y,
				balloon_number: b.balloon_number,
				ballooning_page: b.ballooning_page,
			};
			if (b.id && !b.id.toString().startsWith('temp-')) {
				balloon.id = b.id;
				if (b.skip_review) {
					balloon.skip_review = true;
				}
			}
			return balloon;
		});

		let forceUpdate = false;

		if (projectData.is_locked) {
			try {
				const validationRes = await validateChanges({
					id: projectId!,
					body: { balloons: balloonsPayload },
				}).unwrap();

				if (validationRes.requires_confirmation) {
					const confirmed = await confirmBreakingChanges(validationRes.warnings);

					if (!confirmed) {
						// Revert local state so the user isn't trapped with unsaved edits
						setLocalBalloons(JSON.parse(JSON.stringify(originalBalloons)));
						setDeletedBalloonIds([]);
						return false;
					}
					forceUpdate = true;
				}
			} catch (err) {
				console.error("Validation failed", err);
				message.error("Failed to validate changes.");
				return false;
			}
		}

		const payload = {
			unit,
			balloon_size: balloonSize,
			balloons: balloonsPayload,
			reused_from: reusedFrom,
			force: forceUpdate,
			...(toleranceTableChanged() ? { tolerance_table: toleranceTable } : {}),
			...(toleranceSuggestionChanged()
				? { tolerance_table_suggestion: toleranceSuggestion }
				: {}),
		};

		try {
			const updatedProject = await updateBallooningProject({
				id: projectId!,
				body: payload,
			}).unwrap();
			setDeletedBalloonIds([]);
			await refetch();
			// Explicitly sync original balloons to clear modified state immediately if needed,
			// though useEffect should handle it via projectData update.

			message.success('Project updated successfully!');
			return true;
		} catch (e: any) {
			const errorData = e.data;
			const specificMessage =
				errorData?.balloons?.[0] ||
				errorData?.balloon_number?.[0] ||
				errorData?.tolerance_table?.[0] ||
				errorData?.non_field_errors?.[0] ||
				errorData?.detail;

			message.error(specificMessage || 'Error updating project');
			return false;
		}
	};

	return (
		<div className="flex flex-col w-full h-full pb-2">
			{projectData?.is_locked && ballooningStatus === 'In Progress' && (
				<Alert
					description={
						<span>
							You are in a <strong>limited editing</strong> mode because one or
							more samples have been recorded already. You can only edit one
							balloon at a time.
						</span>
					}
					type="warning"
					showIcon
					className="mx-4 mt-2"
					style={{ padding: '8px 15px' }}
				/>
			)}
			{generationJob?.status === 'failed' &&
				localBalloons.length === 0 &&
				dismissedJobErrorId !== generationJob.id && (
					<Alert
						type="error"
						showIcon
						closable
						onClose={() => setDismissedJobErrorId(generationJob.id)}
						message="Inspection plan generation failed"
						description={
							generationJob.error ||
							'The drawing could not be analyzed. Try again, or balloon it manually.'
						}
						className="mx-4 mt-2"
						style={{ padding: '8px 15px' }}
					/>
				)}
			{projectData?.auto_ballooning_skipped_for && (() => {
				const twin = projectData.auto_ballooning_skipped_for;
				// Judged on the loaded project too, so the modal does not flash open
				// before its balloons are copied into local state.
				const projectIsEmpty =
					localBalloons.length === 0 &&
					!projectData.ballooning_pages?.some(
						(page: { balloons?: Balloon[] }) => page.balloons?.length
					);
				const close = () => setAutoSkipDismissed(true);
				return (
					<Modal
						open={projectIsEmpty && !autoSkipDismissed}
						centered
						title="AI ballooning was skipped for this drawing"
						onCancel={close}
						styles={viewportCappedModalStyles}
						data-testid="auto-ballooning-skipped-modal"
						footer={
							<Space>
								<Button onClick={close}>Close</Button>
								<Button
									icon={<OpenAIOutlined />}
									disabled={
										ballooningStatus === 'Completed' ||
										projectData?.is_locked ||
										generationActive
									}
									onClick={() => {
										close();
										setGenerationOptionsOpen(true);
									}}
								>
									Generate anyway
								</Button>
								<Button
									type="primary"
									icon={<DownloadOutlined />}
									disabled={ballooningStatus === 'Completed'}
									onClick={() => {
										close();
										setIsUserRequest(true);
										setIsModalOpen(true);
									}}
								>
									Import balloons
								</Button>
							</Space>
						}
					>
						<Typography.Paragraph>
							<Typography.Text strong>{twin.name}</Typography.Text>
							{twin.order_name ? ` (order ${twin.order_name})` : ''} is already
							ballooned, so this drawing was not analyzed automatically. Import its
							balloons, or generate the inspection plan to analyze it anyway.
						</Typography.Paragraph>
						{twin.ballooning_project_id && (
							<Link to={`/ballooning/${twin.ballooning_project_id}`} onClick={close}>
								Open that ballooning
							</Link>
						)}
					</Modal>
				);
			})()}
			<div ref={headerRef} className="flex flex-col justify-between">
				<div className="flex flex-row w-full justify-between items-center">
					<Header
						title="Ballooning"
						breadcrumbItems={[
							{ title: <Link to="/">Home</Link> },
							{ title: <Link to="/ballooning">Ballooning</Link> },
							{ title: 'Ballooning Project Details' },
						]}
					/>
					<div className="flex space-x-2 p-2">
						<Button
							onClick={handleLogIssue}
							disabled={isEnsuringProgram || !projectData?.order_line_item?.id}
							loading={isEnsuringProgram}
							icon={<PlusOutlined />}
							style={{ borderRadius: 6 }}
						>
							Log Issue
						</Button>
						{(() => {
							const hasDrawing = (projectData?.ballooning_pages ?? []).some(
								(page: BallooningPage) => !!page.redacted_drawing_page
							);
							const disabledReason = generationActive
								? null
								: localBalloons.length > 0
									? 'Only available on a project with no balloons.'
									: projectData?.is_locked
										? 'Samples have already been measured for this part.'
										: ballooningStatus === 'Completed'
											? 'Set the status back to In Progress first.'
											: !hasDrawing
												? 'This project has no drawing image to analyze.'
												: drawingLoadState === 'error' || drawingLoadState === 'missing'
													? 'The drawing image could not be loaded, so it cannot be analyzed. Reload the page, or check the drawing file.'
													: hasUnsavedChanges()
														? 'Save or discard your changes first.'
														: null;
							const pagesTotal = generationJob?.pages_total ?? 0;
							const progressLabel =
								generationJob?.status === 'running' && pagesTotal > 0
									? `Analyzing page ${Math.min(generationJob.pages_done + 1, pagesTotal)} of ${pagesTotal}`
									: 'Queued…';
							const button = (
								<Button
									onClick={() => setGenerationOptionsOpen(true)}
									disabled={disabledReason !== null || generationActive || !projectData}
									loading={isStartingGeneration || generationActive}
									icon={<OpenAIOutlined />}
									style={{ borderRadius: 6 }}
									aria-label="Generate Inspection Plan"
									data-testid="generate-inspection-plan"
								/>
							);
							return (
								<Tooltip
									title={
										<>
											<div className="font-semibold">
												{generationActive ? progressLabel : 'Generate Inspection Plan'}
											</div>
											{disabledReason ??
												(generationActive
													? 'The drawing is being analyzed. Balloons appear here when it finishes.'
													: 'Detect the dimensions on the drawing with AI and create draft balloons for review.')}
										</>
									}
								>
									<span>{button}</span>
								</Tooltip>
							);
						})()}
						<Tooltip
							title={
								<>
									<div className="font-semibold">
										Tolerance table
										{hasRows(toleranceTable) ? ` (${toleranceTable.rows.length} rows)` : ''}
									</div>
									{toleranceSuggestion
										? 'The drawing shows a different tolerance table. Open to review it.'
										: hasRows(toleranceTable)
											? 'General tolerances for dimensions printed without one.'
											: 'No tolerance table yet. Add one, or generate the inspection plan to read it from the drawing.'}
								</>
							}
						>
							<Badge dot={!!toleranceSuggestion} offset={[-4, 4]}>
								<Button
									onClick={() => setToleranceModalOpen(true)}
									disabled={!projectData}
									icon={<TableOutlined />}
									style={{ borderRadius: 6 }}
									aria-label="Tolerance table"
									data-testid="tolerance-table-button"
								/>
							</Badge>
						</Tooltip>
						<ArcSagittaWidget
							defaultValues={{
								unit: unit === 'MM' ? 'mm' : 'inches',
							}}
							buttonText=""
							tooltip={
								<>
									<div className="font-semibold">Arc sagitta calculator</div>
									Arc height from its radius and chord, with tolerances.
								</>
							}
							buttonProps={{
								style: { borderRadius: 6 },
								'aria-label': 'Arc sagitta calculator',
							}}
						/>
						<Tooltip
							title={
								generationJob ? (
									<>
										<div className="font-semibold">AI analysis stats</div>
										Model, tokens and time of the last AI analysis.
									</>
								) : (
									'This drawing has not been analyzed by AI yet.'
								)
							}
						>
							<Button
								onClick={() => setStatsModalOpen(true)}
								disabled={!generationJob}
								icon={<BarChartOutlined />}
								style={{ borderRadius: 6 }}
								aria-label="AI analysis stats"
								data-testid="generation-stats-button"
							/>
						</Tooltip>
						<ToleranceTableModal
							open={toleranceModalOpen}
							table={toleranceTable}
							suggestion={toleranceSuggestion}
							balloons={localBalloons}
							editable={ballooningStatus !== 'Completed' && !projectData?.is_locked}
							onApply={handleApplyToleranceTable}
							onDismissSuggestion={() => setToleranceSuggestion(null)}
							onCancel={() => setToleranceModalOpen(false)}
						/>
						{import.meta.env.DEV && (
							<>
								<input
									ref={devDrawingInputRef}
									type="file"
									accept=".pdf,image/png,image/jpeg,image/tiff"
									style={{ display: 'none' }}
									onChange={handleDevDrawingSelected}
								/>
								<Tooltip
									title={
										<>
											<div className="font-semibold">Upload drawing (dev)</div>
											{ballooningStatus === 'Completed'
												? 'Ballooning is completed. Set it back to In Progress to replace the drawing.'
												: <>Development only: replace this project&apos;s pages with your own
													drawing (PDF or image), stored in the bucket set by AWS_BUCKET_NAME.</>}
										</>
									}
								>
									<Button
										onClick={() => devDrawingInputRef.current?.click()}
										loading={isUploadingDrawing}
										disabled={
											!projectData || generationActive || ballooningStatus === 'Completed'
										}
										icon={<UploadOutlined />}
										style={{ borderRadius: 6 }}
										aria-label="Upload drawing (dev)"
										data-testid="dev-upload-drawing"
									/>
								</Tooltip>
							</>
						)}
						{(() => {
							const previousCount =
								projectData?.previous_ballooning_projects?.length ?? 0;
							const hasLocalBalloons = localBalloons.length > 0;
							// An empty candidate list is no longer a reason to disable: the
							// picker can search any ballooned part, which is the only option
							// for a part ordered for the first time. Clobbering existing work
							// still is.
							const disabledReason = hasLocalBalloons
								? 'Current project must be blank to import data from a previous order.'
								: null;

							const buttonLabel =
								previousCount > 0 && !hasLocalBalloons
									? `Import from previous order (${previousCount})`
									: 'Import from a previous or similar part';

							const button = (
								<Badge
									count={previousCount > 0 && !hasLocalBalloons ? previousCount : 0}
									size="small"
									offset={[-4, 4]}
									color="blue"
								>
									<Button
										onClick={() => {
											setIsUserRequest(true);
											setIsModalOpen(true);
										}}
										disabled={disabledReason !== null}
										icon={<CopyOutlined />}
										style={{ borderRadius: 6 }}
										aria-label={buttonLabel}
									/>
								</Badge>
							);

							const hintTitle =
								disabledReason ??
								(previousCount === 0
									? 'No previous order is matched to this part. Search by part or order name to reuse a comparable one — you will be asked to confirm you checked the values.'
									: null);

							// Offer the backfill only when nothing matched AND there is no
							// local work: it recomputes the matched pool, which is pointless
							// once the operator has their own balloons.
							const canRefresh = !hasLocalBalloons && previousCount === 0;

							const handleRefreshPreviousOrders = async () => {
								if (!projectId) return;
								try {
									await refreshPreviousOrders({
										id: projectId,
									}).unwrap();
									notification.success({
										message: 'Previous orders refresh requested',
										description:
											'The recomputation is running in the background. Refresh this page in a few seconds — the import button will update if matching previous orders are found.',
										placement: 'topRight',
									});
								} catch (err: any) {
									notification.error({
										message: 'Failed to refresh previous orders',
										description:
											err?.data?.detail ||
											'Unable to queue the backfill request. Try again or contact engineering.',
										placement: 'topRight',
									});
								}
							};

							return (
								<>
									{hintTitle ? (
										<Tooltip title={hintTitle}>
											<span>{button}</span>
										</Tooltip>
									) : (
										button
									)}
									{canRefresh && (
										<Tooltip title="Recompute the matched previous orders for this sales order. Use when you know an identical part was ballooned before but it is not being offered.">
											<Button
												onClick={handleRefreshPreviousOrders}
												loading={isRefreshingPreviousOrders}
												icon={<ReloadOutlined />}
												style={{ borderRadius: 6 }}
												aria-label="Refresh previous orders"
											/>
										</Tooltip>
									)}
								</>
							);
						})()}
						<Select
							value={ballooningStatus}
							onChange={(value) => updateBallooningStatus(value)}
							onDropdownVisibleChange={(open) => {
								if (
									open &&
									ballooningStatus !== 'Completed' &&
									!unreviewedCount &&
									incompleteBalloons.length > 0
								) {
									showMissingFields();
								}
							}}
							disabled={isLoading}
							style={{
								width: 180,
								marginRight: 8,
								backgroundColor: '#52c41a',
								color: '#fff',
								borderColor: '#52c41a',
								borderRadius: 6,
							}}
							suffixIcon={<CheckCircleOutlined style={{ color: '#fff' }} />}
							dropdownStyle={{
								borderRadius: 6,
							}}
						>
							<Option value="In Progress">
								<div className="flex items-center">
									<SyncOutlined style={{ marginRight: 8, color: '#1890ff' }} />
									In Progress
								</div>
							</Option>
							<Option
								value="Completed"
								disabled={ballooningStatus !== 'Completed' && !!completionBlocker}
							>
								<Tooltip
									title={
										ballooningStatus !== 'Completed'
											? (completionBlocker ?? undefined)
											: undefined
									}
									placement="left"
								>
									<div className="flex items-center">
										<CheckCircleOutlined
											style={{ marginRight: 8, color: '#52c41a' }}
										/>
										Completed
									</div>
								</Tooltip>
							</Option>
						</Select>

						<Tooltip
							title={
								!unit ? 'Select a unit before saving.' : undefined
							}
						>
							<span style={{ display: 'inline-block' }}>
								<Button
									type="primary"
									onClick={handleSave}
									disabled={isLoading || !unit}
									icon={<SaveOutlined />}
									style={{
										borderRadius: 6,
									}}
								>
									Save Changes
								</Button>
							</span>
						</Tooltip>
					</div>
				</div>
				<div className="flex flex-row w-full mb-3">
					<div className="flex flex-row items-center gap-1">
						<Select
							value={layout}
							onChange={(value) => {
								setLayout(value);
								localStorage.setItem('ballooningLayout', value);
							}}
							options={[
								{ label: 'Vertical', value: 'vertical' },
								{ label: 'Horizontal', value: 'horizontal' },
							]}
						/>
						<Select
							value={unit}
							style={{ width: '150px' }}
							options={unitOptions}
							onChange={handleChangeUnit}
							status={unitError ? 'error' : ''}
						/>
						<Select
							value={balloonSize}
							style={{ width: '200px' }}
							options={balloonSizeOptions}
							onChange={handleChangeBalloonSize}
						/>
						<div className="flex flex-row items-center gap-2 ml-2">
							<Link
								target="_blank"
								rel="noopener noreferrer"
								to={`/orders/${projectData?.order_line_item?.order?.id}`}
							>
								{projectData?.order_line_item?.order?.name}
							</Link>
							<span className="text-gray-400">|</span>
							<Link
								target="_blank"
								rel="noopener noreferrer"
								to={`/orders/line-items/${projectData?.order_line_item?.id}`}
							>
								{projectData?.order_line_item?.name}
							</Link>
							{countUnreviewed(localBalloons) > 0 && (
								<Tooltip title="Auto-generated balloons nobody has approved or edited yet.">
									<Tag
										color="orange"
										icon={<OpenAIOutlined />}
										data-testid="unreviewed-balloon-count"
									>
										{countUnreviewed(localBalloons)} need review
									</Tag>
								</Tooltip>
							)}
							{uniqueViews.length > 0 && (
								<Space wrap>
									| Keyence Program Names:
									{uniqueViews.map((view) => (
										<Button
											icon={<CopyOutlined />}
											key={view}
											type="default"
											onClick={() => handleCopy(view)}
										>
											{view.toUpperCase()}
										</Button>
									))}
								</Space>
							)}
						</div>
					</div>
				</div>
			</div>

			<div
				style={{
					height: `calc(100vh - ${headerRef?.current?.clientHeight}px - 1rem)`,
				}}
			>
				<Splitter className="gap-1 w-full h-full" layout={layout} key={layout}>
					<Splitter.Panel min={300} className="flex h-full gap-1">
						<div className="relative isolate flex-1 h-full overflow-hidden border bg-white">
							<div className="absolute right-2 top-2 z-20 flex flex-col items-end gap-1">
								<Tooltip
									placement="bottomRight"
									title="Draw a line from each balloon to the dimension it was generated from."
								>
									<label className="flex cursor-pointer select-none items-center gap-2 rounded-md border border-gray-200 bg-white/90 px-2 py-1 text-xs text-gray-700 shadow-sm">
										<PiLineSegment className="text-base" />
										Leader Lines
										<Switch
											size="small"
											checked={showLeaderLines}
											onChange={setShowLeaderLines}
											aria-label="Show leader lines"
											data-testid="leader-lines-switch"
										/>
									</label>
								</Tooltip>
								{projectData?.ballooning_pages?.some(
									(page: BallooningPage) => page.view_outlines?.views?.length
								) && (
										<Tooltip
											placement="bottomRight"
											title="Show the traced outline of each view. Hover a balloon to highlight its view."
										>
											<label className="flex cursor-pointer select-none items-center gap-2 rounded-md border border-gray-200 bg-white/90 px-2 py-1 text-xs text-gray-700 shadow-sm">
												<PiPolygon className="text-base" />
												Outlines
												<Switch
													size="small"
													checked={showViewOutlines}
													onChange={setShowViewOutlines}
													aria-label="Show view outlines"
													data-testid="view-outlines-switch"
												/>
											</label>
										</Tooltip>
									)}
							</div>
							<BallooningViewer
								editable={ballooningStatus !== 'Completed'}
								activeBalloonId={activeBalloonId}
								setActiveBalloonId={setActiveBalloonId}
								centerOnActive={centerOnActive}
								setCenterOnActive={setCenterOnActive}
								localBalloons={localBalloons}
								fallbackToSavedBalloons={false}
								setLocalBalloons={setLocalBalloons}
								balloonSize={balloonSize}
								pageID={pageID}
								pageNumber={pageNumber}
								setPageNumber={setPageNumber}
								isTabletView={false}
								allowedEditableBalloonId={allowedEditableBalloonId}
								showReviewAids
								showViewOutlines={showViewOutlines}
								showLeaderLines={showLeaderLines}
								onImageLoadStateChange={setDrawingLoadState}
								onDeleteBalloon={(balloon) =>
									balloonTableRef.current?.deleteBalloon(balloon.id)
								}
								renderBalloonPopup={(balloon, close) => (
									<BalloonQuickEdit
										balloon={balloon}
										editable={isBalloonEditable(balloon)}
										onChange={(updates) => handleQuickEdit(balloon.id, updates)}
										onStrategyChange={(value, option) =>
											balloonTableRef.current?.changeStrategy(balloon.id, value, option)
										}
										onToolChange={(value) =>
											balloonTableRef.current?.changeTool(balloon.id, value)
										}
										keyenceBuildingId={projectData?.keyence_building?.id ?? null}
										onApprove={
											balloon.id.toString().startsWith('temp-')
												? undefined
												: () => handleApproveBalloons([balloon.id])
										}
										onDelete={() => balloonTableRef.current?.deleteBalloon(balloon.id)}
										onClose={close}
									/>
								)}
							/>
						</div>
						<BalloonReviewPanel
							balloons={localBalloons}
							activeBalloonId={activeBalloonId}
							onSelect={(balloon) => {
								setActiveBalloonId(balloon.id);
								setCenterOnActive(true);
								setPageNumber(balloon.ballooning_page_number);
							}}
							isEditable={isBalloonEditable}
							onChange={handleQuickEdit}
							onStrategyChange={(id, value, option) =>
								balloonTableRef.current?.changeStrategy(id, value, option)
							}
							onToolChange={(id, value) => balloonTableRef.current?.changeTool(id, value)}
							keyenceBuildingId={projectData?.keyence_building?.id ?? null}
							onApprove={(id) => handleApproveBalloons([id])}
							onDelete={(id) => balloonTableRef.current?.deleteBalloon(id)}
							open={reviewPanelOpen}
							onOpenChange={handleReviewPanelOpen}
						/>
					</Splitter.Panel>
					<Splitter.Panel min={200} className="overflow-hidden border bg-white">
						<BalloonTable
							ref={balloonTableRef}
							activeBalloonId={activeBalloonId}
							setActiveBalloonId={setActiveBalloonId}
							setCenterOnActive={() => setCenterOnActive(true)}
							localBalloons={localBalloons}
							setLocalBalloons={setLocalBalloons}
							setDeletedBalloonIds={setDeletedBalloonIds}
							scrollY={tablePanelRef?.current?.clientHeight}
							pageNumber={pageNumber}
							setPageNumber={setPageNumber}
							editable={ballooningStatus !== 'Completed'}
							keyenceBuildingId={projectData?.keyence_building?.id ?? null}
							toleranceTable={toleranceTable}
							allowedEditableBalloonId={allowedEditableBalloonId}
							disableDragging={projectData?.is_locked}
							onApproveBalloons={handleApproveBalloons}
						/>
					</Splitter.Panel>
				</Splitter>
			</div>
			<GenerationStatsModal
				open={statsModalOpen}
				job={generationJob}
				onClose={() => setStatsModalOpen(false)}
			/>
			<Modal
				className="cam-modal"
				title="Ballooning Reusability Checker"
				open={isModalOpen}
				onCancel={() => setIsModalOpen(false)}
				footer={null}
				centered
				width="94vw"
				destroyOnClose
				styles={{
					content: {
						display: 'flex',
						flexDirection: 'column',
						height: 'calc(100vh - 48px)',
						maxHeight: 'calc(100vh - 48px)',
					},
					body: {
						flex: '1 1 auto',
						minHeight: 0,
						overflow: 'hidden',
						padding: 0,
					},
				}}
			>
				<BallooningReusabilityChecker
					orderLineItem={projectData?.order_line_item}
					isUserRequest={isUserRequest}
					onClose={() => setIsModalOpen(false)}
					onApprove={(balloons, reusedFromId) =>
						handleApproval(balloons, reusedFromId)
					}
				/>
			</Modal>
			{issueModalProgramId && (
				<IssueFormModal
					programId={issueModalProgramId}
					issue={null}
					open
					onClose={() => setIssueModalProgramId(null)}
				/>
			)}

			<Modal
				title="Inspection Plan Completed"
				open={completionModalOpen}
				centered
				footer={null}
				onCancel={() => setCompletionModalOpen(false)}
				width={420}
			>
				<p style={{ marginBottom: 16 }}>Where would you like to go?</p>
				<Space direction="vertical" style={{ width: '100%' }}>
					<Button
						type="primary"
						block
						onClick={() => navigate(`/orders/line-items/${projectData?.order_line_item?.id}`)}
					>
						Line Item Details
					</Button>
					<Button
						block
						onClick={() => navigate('/production/queues')}
					>
						Production Queue
					</Button>
				</Space>
			</Modal>

			<GenerationOptionsModal
				open={generationOptionsOpen}
				loading={isStartingGeneration}
				onCancel={() => setGenerationOptionsOpen(false)}
				onGenerate={handleGenerateBalloons}
			/>

			<GenerationProgressModal
				open={generationActive}
				job={generationJob}
				cancelling={isCancellingGeneration}
				onCancelGeneration={handleCancelGeneration}
			/>
		</div >
	);
};

export default BallooningPage;
